import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaCheck, FaTimes, FaPhone } from 'react-icons/fa';
import { fmt, fmtM, fmtUnit, fmtDate } from '../utils/helpers';
import { TableExportBar } from '../shared/index';
import { exportExcel, rowsToHtml, copyHtmlToClipboard } from '../utils/exports';

const PAGE_SIZE = 10;

// Exclusion filters (B-logic): désactiver = cacher les lots avec cette valeur > 0
const EXCL_FILTERS = [
  { key: 'encaisse', label: 'Encaissé',  color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7', test: r => r.encaisse > 0 },
  { key: 'recept',   label: 'Réception', color: '#1565c0', bg: '#e3f2fd', border: '#90caf9', test: r => r.recept   > 0 },
  { key: 'ptf',      label: 'PTF',       color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8', test: r => r.ptf     > 0 },
  { key: 'impaye',   label: 'Impayé',    color: '#c62828', bg: '#ffebee', border: '#ef9a9a', test: r => r.impaye  > 0 },
  { key: 'autres',   label: 'Autres',    color: '#546e7a', bg: '#eceff1', border: '#b0bec5', test: r => r.autres  > 0 },
];
const RELIQUAT_FILTER = { key: 'reliquat', label: 'Reliquat', color: '#e65100', bg: '#fff3e0', border: '#ffcc80', test: r => r.reliquat > 10 };
const ALL_FILTERS = [RELIQUAT_FILTER, ...EXCL_FILTERS];

// Filtres mode de règlement (inclusion OR : sélectionner = montrer les lots ayant ce mode > 0)
const MODE_FILTERS = [
  { key: 'mr_cheque',       label: 'Chèque',      color: '#1565c0', bg: '#e3f2fd', border: '#90caf9' },
  { key: 'mr_lcn',          label: 'LCN',          color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
  { key: 'mr_virement',     label: 'Virement',     color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
  { key: 'mr_compensation', label: 'Compensation', color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
  { key: 'mr_credit',       label: 'Crédit',       color: '#00838f', bg: '#e0f7fa', border: '#80deea' },
  { key: 'mr_versement',    label: 'Versement',    color: '#827717', bg: '#f9fbe7', border: '#dce775' },
  { key: 'mr_autres',       label: 'Autres modes', color: '#546e7a', bg: '#eceff1', border: '#b0bec5' },
];

const STATUT_CFG = {
  NOT_YET:  { label: 'Pas encore signé',    bg: '#f3e5f5', color: '#6a1b9a', border: '#ce93d8' },
  CHEZ:     { label: 'Signé client',         bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
  EN_COURS: { label: 'Signature en cours',   bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  SIGNE:    { label: 'Signé notaire',        bg: '#e8f5e9', color: '#1b5e20', border: '#a5d6a7' },
};
const getStatutKey = r => r.statut_notaire || 'NOT_YET';

const TH = { padding: '7px 10px', fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: '#4a505c', background: '#f5f4f2', borderBottom: '2px solid #d4c8bc', whiteSpace: 'nowrap' };
const TD = { padding: '7px 10px', fontSize: '0.76rem', color: '#333', borderBottom: '1px solid #ede9e4', verticalAlign: 'middle' };

const TelCell = ({ tels }) => {
  if (!tels) return <span style={{ color: '#bbb' }}>—</span>;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {tels.split(' / ').filter(Boolean).map((tel, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '0.72rem' }}>{tel}</span>
          <button onClick={() => navigator.clipboard?.writeText(tel)} title="Copier"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: 0, fontSize: '0.65rem', lineHeight: 1 }}>⎘</button>
          <a href={`tel:${tel}`} title="Appeler" style={{ color: '#2e7d32', lineHeight: 1 }}><FaPhone size={9} /></a>
        </span>
      ))}
    </span>
  );
};

const KpiCard = ({ label, value, color }) => (
  <div style={{ flex: '1 1 160px', background: '#fff', border: '2px solid #d4c8bc', borderRadius: 8, padding: '10px 14px', borderTop: `3px solid ${color}` }}>
    <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '1rem', fontWeight: 700, color }}>{value}</div>
  </div>
);

const StatutLotsSection = ({ rows = [], loading, unit = 'default' }) => {
  const [activeFilters, setActiveFilters] = useState(() => new Set(EXCL_FILTERS.map(f => f.key)));
  const [reliquatEtat, setReliquatEtat]   = useState(null);
  const [tfFilter, setTfFilter]           = useState(null);
  const [statutFilter, setStatutFilter]   = useState(new Set(Object.keys(STATUT_CFG)));
  const [notaireFilter, setNotaireFilter] = useState(new Set());
  const [projetFilter, setProjetFilter]           = useState(new Set());
  const [beneficiaireFilter, setBeneficiaireFilter]     = useState(new Set());
  const [showAllBeneficiaires, setShowAllBeneficiaires] = useState(false);
  const [modeFilter, setModeFilter]               = useState(new Set());
  const [anneeComptaFilter, setAnneeComptaFilter] = useState(new Set());
  const [viewMode, setViewMode] = useState('resume');
  const [sortKey, setSortKey]   = useState('projet');
  const [sortDir, setSortDir]   = useState(1);
  const [page, setPage]         = useState(1);

  const toggleFilter = (key) => {
    setActiveFilters(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
    setPage(1);
  };
  const toggleProjet = (nom) => {
    setProjetFilter(prev => { const next = new Set(prev); next.has(nom) ? next.delete(nom) : next.add(nom); return next; });
    setPage(1);
  };
  const toggleBeneficiaire = (nom) => {
    setBeneficiaireFilter(prev => { const next = new Set(prev); next.has(nom) ? next.delete(nom) : next.add(nom); return next; });
    setPage(1);
  };
  const toggleMode = (key) => {
    setModeFilter(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
    setPage(1);
  };
  const toggleAnneeCompta = (yr) => {
    setAnneeComptaFilter(prev => { const next = new Set(prev); next.has(yr) ? next.delete(yr) : next.add(yr); return next; });
    setPage(1);
  };

  /* étape 1 : filtre catégories */
  const categoryFiltered = useMemo(() => {
    let result = rows.filter(r => EXCL_FILTERS.every(f => activeFilters.has(f.key) || !f.test(r)));
    if (reliquatEtat === 'avec') result = result.filter(r => r.reliquat > 10);
    else if (reliquatEtat === 'sans') result = result.filter(r => r.reliquat <= 0);
    return result;
  }, [rows, activeFilters, reliquatEtat]);

  /* étape 2 : filtre statut notaire */
  const statutFiltered = useMemo(() => {
    if (statutFilter.size === Object.keys(STATUT_CFG).length) return categoryFiltered;
    return categoryFiltered.filter(r => statutFilter.has(getStatutKey(r)));
  }, [categoryFiltered, statutFilter]);

  /* étape 3 : filtre notaire */
  const notaireFiltered = useMemo(() => {
    if (notaireFilter.size === 0) return statutFiltered;
    return statutFiltered.filter(r => notaireFilter.has(r.notaire_id ?? 'none'));
  }, [statutFiltered, notaireFilter]);

  /* étape 4 : filtre TF */
  const tfFiltered = useMemo(() => {
    if (!tfFilter) return notaireFiltered;
    return notaireFiltered.filter(r => (r.disponibilite_tf || '').toLowerCase() === tfFilter);
  }, [notaireFiltered, tfFilter]);

  const allProjets = useMemo(() => {
    const s = new Set(rows.map(r => r.projet || '—'));
    return [...s].sort();
  }, [rows]);

  /* étape 5 : filtre projets */
  const projetFiltered = useMemo(() => {
    if (projetFilter.size === 0) return tfFiltered;
    return tfFiltered.filter(r => projetFilter.has(r.projet || '—'));
  }, [tfFiltered, projetFilter]);

  /* étape 6 : filtre année compta */
  const anneeComptaFiltered = useMemo(() => {
    if (anneeComptaFilter.size === 0) return projetFiltered;
    return projetFiltered.filter(r => {
      const k = r.annee_compta != null ? String(r.annee_compta) : 'N/A';
      return anneeComptaFilter.has(k);
    });
  }, [projetFiltered, anneeComptaFilter]);

  /* étape 7 : filtre bénéficiaire */
  const beneficiaireFiltered = useMemo(() => {
    if (beneficiaireFilter.size === 0) return anneeComptaFiltered;
    return anneeComptaFiltered.filter(r => beneficiaireFilter.has(r.beneficiaire || ''));
  }, [anneeComptaFiltered, beneficiaireFilter]);

  /* étape 8 : filtre mode de règlement (OR — au moins un mode sélectionné présent > 0) */
  const filtered = useMemo(() => {
    if (modeFilter.size === 0) return beneficiaireFiltered;
    return beneficiaireFiltered.filter(r => MODE_FILTERS.some(f => modeFilter.has(f.key) && (r[f.key] || 0) > 0));
  }, [beneficiaireFiltered, modeFilter]);

  /* ── KPI ── */
  const kpi = useMemo(() => ({
    nb:        filtered.length,
    prixTotal: filtered.reduce((s, r) => s + (r.prix_net  || 0), 0),
    avance:    filtered.reduce((s, r) => s + (r.avance    || 0), 0),
    reliquat:  filtered.reduce((s, r) => s + (r.reliquat  || 0), 0),
  }), [filtered]);

  const filterCounts = useMemo(() => {
    const counts = {};
    ALL_FILTERS.forEach(f => { counts[f.key] = filtered.filter(f.test).length; });
    return counts;
  }, [filtered]);

  // anneeComptaChips : depuis projetFiltered (avant filtre année compta)
  const anneeComptaChips = useMemo(() => {
    const map = {};
    projetFiltered.forEach(r => {
      const k = r.annee_compta != null ? String(r.annee_compta) : 'N/A';
      if (!map[k]) map[k] = { year: k, count: 0 };
      map[k].count++;
    });
    return Object.values(map).sort((a, b) => a.year.localeCompare(b.year));
  }, [projetFiltered]);

  // beneficiaireChips : depuis anneeComptaFiltered (avant filtre bénéficiaire)
  const beneficiaireChips = useMemo(() => {
    const map = {};
    anneeComptaFiltered.forEach(r => {
      const k = r.beneficiaire || '';
      if (!k) return;
      if (!map[k]) map[k] = { nom: k, count: 0 };
      map[k].count++;
    });
    return Object.values(map).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  }, [anneeComptaFiltered]);

  // modeCounts : depuis beneficiaireFiltered (avant filtre mode de règlement)
  const modeCounts = useMemo(() => {
    const counts = {};
    MODE_FILTERS.forEach(f => { counts[f.key] = beneficiaireFiltered.filter(r => (r[f.key] || 0) > 0).length; });
    return counts;
  }, [beneficiaireFiltered]);

  // notaireChips depuis statutFiltered
  const notaireChips = useMemo(() => {
    const map = {};
    statutFiltered.filter(r => getStatutKey(r) !== 'NOT_YET').forEach(r => {
      const key = r.notaire_id ?? 'none';
      if (!map[key]) map[key] = { key, label: r.notaire_nom || 'Sans notaire', count: 0 };
      map[key].count++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [statutFiltered]);

  // projetChips depuis tfFiltered
  const projetChips = useMemo(() => {
    const map = {};
    allProjets.forEach(nom => { map[nom] = { nom, count: 0 }; });
    tfFiltered.forEach(r => { const k = r.projet || '—'; if (map[k]) map[k].count++; });
    return Object.values(map).filter(p => p.count > 0).sort((a, b) => b.count - a.count);
  }, [allProjets, tfFiltered]);

  /* ── données "En cours commercial" : tous filtres sauf TF ── */
  const filteredForEncours = useMemo(() => {
    let result = notaireFiltered;
    if (projetFilter.size > 0)       result = result.filter(r => projetFilter.has(r.projet || '—'));
    if (anneeComptaFilter.size > 0)  result = result.filter(r => { const k = r.annee_compta != null ? String(r.annee_compta) : 'N/A'; return anneeComptaFilter.has(k); });
    if (beneficiaireFilter.size > 0) result = result.filter(r => beneficiaireFilter.has(r.beneficiaire || ''));
    if (modeFilter.size > 0)         result = result.filter(r => MODE_FILTERS.some(f => modeFilter.has(f.key) && (r[f.key] || 0) > 0));
    return result;
  }, [notaireFiltered, projetFilter, anneeComptaFilter, beneficiaireFilter, modeFilter]);

  const enCoursRows = useMemo(() => {
    const map = {};
    const empty = () => ({ nb: 0, prixNet: 0, avance: 0, reliquat: 0 });
    filteredForEncours.forEach(r => {
      const k = r.projet || '—';
      if (!map[k]) map[k] = { projet: k, titre: empty(), nonTitre: empty(), prochainement: empty() };
      const tf = (r.disponibilite_tf || '').toLowerCase();
      const grp = tf === 'oui' ? map[k].titre : tf === 'prochainement' ? map[k].prochainement : map[k].nonTitre;
      grp.nb++; grp.prixNet += r.prix_net || 0; grp.avance += r.avance || 0; grp.reliquat += r.reliquat || 0;
    });
    return Object.values(map).sort((a, b) =>
      (b.titre.nb + b.nonTitre.nb + b.prochainement.nb) - (a.titre.nb + a.nonTitre.nb + a.prochainement.nb)
    );
  }, [filteredForEncours]);

  /* ── résumé par projet ── */
  const resumeRows = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const k = r.projet || '—';
      if (!map[k]) map[k] = { projet: k, nb: 0, prixNet: 0, avance: 0, reliquat: 0, encaisse: 0, recept: 0, ptf: 0, impaye: 0, autres: 0 };
      const v = map[k];
      v.nb++;      v.prixNet  += r.prix_net  || 0; v.avance   += r.avance   || 0;
      v.reliquat  += r.reliquat  || 0; v.encaisse += r.encaisse || 0; v.recept   += r.recept   || 0;
      v.ptf       += r.ptf       || 0; v.impaye   += r.impaye   || 0; v.autres   += r.autres   || 0;
    });
    return Object.values(map).sort((a, b) => b.reliquat - a.reliquat);
  }, [filtered]);

  /* ── détail trié + paginé ── */
  const NUMERIC_FIELDS = new Set(['prix_net','avance','reliquat','encaisse','recept','ptf','impaye','autres','contrat_id',
    'mr_cheque','mr_lcn','mr_virement','mr_compensation','mr_credit','mr_versement','mr_autres','annee_compta']);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1; if (vb == null) return -1;
      if (typeof va === 'string') return sortDir * va.localeCompare(vb, 'fr');
      return sortDir * (va - vb);
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pagedRows  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const onSort = (k) => {
    setSortKey(prev => {
      if (prev === k) setSortDir(d => -d);
      else setSortDir(NUMERIC_FIELDS.has(k) ? -1 : 1);
      return k;
    });
    setPage(1);
  };

  const ThS = ({ label, field, right }) => (
    <th onClick={() => onSort(field)} style={{ ...TH, textAlign: right ? 'right' : 'left', cursor: 'pointer', color: sortKey === field ? '#264061' : '#4a505c' }}>
      {label} <span style={{ fontSize: '0.55rem', opacity: sortKey === field ? 1 : 0.25 }}>{sortKey === field ? (sortDir > 0 ? '▲' : '▼') : '⇅'}</span>
    </th>
  );

  if (loading) return <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div>;
  if (!rows.length) return <div style={{ textAlign: 'center', padding: 30, color: '#aaa', fontSize: '0.85rem' }}>Aucun lot</div>;

  return (
    <div>
      {/* ── KPI cards ── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <KpiCard label="Nb lots"     value={kpi.nb}               color="#4a505c" />
        <KpiCard label="Prix total"  value={fmtUnit(kpi.prixTotal, unit)}  color="#264061" />
        <KpiCard label="Avance"      value={fmtUnit(kpi.avance, unit)}     color="#2e7d32" />
        <KpiCard label="Reliquat"    value={fmtUnit(kpi.reliquat, unit)}   color="#c62828" />
      </div>

      {/* ── Filtre TF ── */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600 }}>Titre Foncier :</span>
        {[
          { val: 'oui',           label: 'Titré',          color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
          { val: 'non',           label: 'Non titré',      color: '#c62828', bg: '#ffebee', border: '#ef9a9a' },
          { val: 'prochainement', label: 'Prochainement',  color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
        ].map(opt => {
          const active = tfFilter === opt.val;
          return (
            <button key={opt.val} onClick={() => setTfFilter(prev => prev === opt.val ? null : opt.val)}
              style={{ background: active ? opt.color : opt.bg, color: active ? '#fff' : opt.color, border: `1.5px solid ${active ? opt.color : opt.border}`, borderRadius: 20, padding: '3px 12px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
              {opt.label}
            </button>
          );
        })}
        {tfFilter && (
          <button onClick={() => setTfFilter(null)}
            style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#c62828', display: 'flex', alignItems: 'center' }}>
            <FaTimes size={10} />
          </button>
        )}
      </div>

      {/* ── Filtre statut notaire ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#7b3a9a', textTransform: 'uppercase', letterSpacing: 0.8, marginRight: 4 }}>
          🏛 Statut notaire
        </span>
        {Object.entries(STATUT_CFG).map(([key, cfg]) => {
          const active = statutFilter.has(key);
          const count  = filtered.filter(r => getStatutKey(r) === key).length;
          return (
            <button key={key}
              onClick={() => setStatutFilter(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; })}
              style={{ padding: '4px 12px', borderRadius: 20, fontSize: '0.71rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', border: `1.5px solid ${active ? '#7b3a9a' : '#d8cef0'}`, background: active ? '#7b3a9a' : '#f7f3ff', color: active ? '#fff' : '#7b3a9a', display: 'flex', alignItems: 'center', gap: 6 }}>
              {cfg.label}
              <span style={{ fontSize: '0.62rem', fontWeight: 700, borderRadius: 10, padding: '0 5px', background: active ? 'rgba(255,255,255,0.25)' : '#e8ddf5', color: active ? '#fff' : '#9a5ab8' }}>{count}</span>
            </button>
          );
        })}
        <button onClick={() => setStatutFilter(new Set(Object.keys(STATUT_CFG)))} title="Tout activer"
          style={{ background: 'none', border: '1px solid #d8cef0', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#7b3a9a', display: 'flex', alignItems: 'center' }}>
          <FaCheck size={10} />
        </button>
        <button onClick={() => setStatutFilter(new Set())} title="Tout désactiver"
          style={{ background: 'none', border: '1px solid #d8cef0', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#9a5ab8', display: 'flex', alignItems: 'center' }}>
          <FaTimes size={10} />
        </button>
      </div>

      {/* ── Chips notaires ── */}
      {notaireChips.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#9a7ab8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
            🏛 Filtrer par notaire
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {notaireChips.map(n => {
              const active = notaireFilter.has(n.key);
              return (
                <button key={n.key}
                  onClick={() => setNotaireFilter(prev => { const next = new Set(prev); next.has(n.key) ? next.delete(n.key) : next.add(n.key); return next; })}
                  style={{ padding: '4px 12px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', border: `1.5px solid ${active ? '#7b3a9a' : '#d8cef0'}`, background: active ? '#7b3a9a' : '#f7f3ff', color: active ? '#fff' : '#7b3a9a', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {n.label}
                  <span style={{ fontSize: '0.62rem', fontWeight: 700, borderRadius: 10, padding: '0 5px', background: active ? 'rgba(255,255,255,0.25)' : '#e8ddf5', color: active ? '#fff' : '#9a5ab8' }}>{n.count}</span>
                </button>
              );
            })}
            {notaireFilter.size > 0 && (
              <button onClick={() => setNotaireFilter(new Set())}
                style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', background: 'none', border: '1px solid #ccc', color: '#999' }}>
                ✕ Tous
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Filtre statut encaissement ── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, marginRight: 2 }}>Filtrer :</span>
        {(() => {
          const ETATS = [
            { val: null,   label: 'Reliquat : Tout', color: '#90867d', bg: '#f5f4f2', border: '#d4c8bc' },
            { val: 'avec', label: 'Reliquat : Avec', color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
            { val: 'sans', label: 'Reliquat : Sans', color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
          ];
          const cur  = ETATS.find(e => e.val === reliquatEtat) || ETATS[0];
          const next = ETATS[(ETATS.indexOf(cur) + 1) % ETATS.length];
          const active = cur.val !== null;
          return (
            <button onClick={() => { setReliquatEtat(next.val); setPage(1); }} title={`Cliquer pour : ${next.label}`}
              style={{ background: active ? cur.color : cur.bg, color: active ? '#fff' : cur.color, border: `1.5px solid ${active ? cur.color : cur.border}`, borderRadius: 20, padding: '3px 12px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
              {cur.label}
            </button>
          );
        })()}
        {EXCL_FILTERS.map(f => {
          const active = activeFilters.has(f.key);
          return (
            <button key={f.key} onClick={() => toggleFilter(f.key)}
              style={{ background: active ? f.color : f.bg, color: active ? '#fff' : f.color, border: `1.5px solid ${active ? f.color : f.border}`, borderRadius: 20, padding: '3px 12px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
              {f.label}
              <span style={{ marginLeft: 5, fontSize: '0.6rem', opacity: 0.8 }}>({filterCounts[f.key]})</span>
            </button>
          );
        })}
        <button onClick={() => setActiveFilters(new Set(EXCL_FILTERS.map(f => f.key)))} title="Tout activer"
          style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#2e7d32', display: 'flex', alignItems: 'center' }}>
          <FaCheck size={10} />
        </button>
        <button onClick={() => setActiveFilters(new Set())} title="Tout désactiver"
          style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#c62828', display: 'flex', alignItems: 'center' }}>
          <FaTimes size={10} />
        </button>
        <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: '#aaa' }}>{filtered.length} lot{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* ── Filtre mode de règlement ── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, marginRight: 2 }}>Mode règlement :</span>
        {MODE_FILTERS.map(f => {
          const active = modeFilter.has(f.key);
          const count  = modeCounts[f.key] || 0;
          if (count === 0 && !active) return null;
          return (
            <button key={f.key} onClick={() => toggleMode(f.key)}
              style={{ background: active ? f.color : f.bg, color: active ? '#fff' : f.color, border: `1.5px solid ${active ? f.color : f.border}`, borderRadius: 20, padding: '3px 12px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
              {f.label}
              <span style={{ marginLeft: 5, fontSize: '0.6rem', opacity: 0.8 }}>({count})</span>
            </button>
          );
        })}
        {modeFilter.size > 0 && (
          <button onClick={() => { setModeFilter(new Set()); setPage(1); }}
            style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#c62828', display: 'flex', alignItems: 'center' }}>
            <FaTimes size={10} />
          </button>
        )}
      </div>

      {/* ── Chips projets ── */}
      {allProjets.length > 1 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, marginRight: 2 }}>Projets :</span>
          {projetChips.map(p => {
            const active = projetFilter.has(p.nom);
            return (
              <button key={p.nom} onClick={() => toggleProjet(p.nom)}
                style={{ background: active ? '#4a505c' : '#f5f4f2', color: active ? '#fff' : '#4a505c', border: `1.5px solid ${active ? '#4a505c' : '#d4c8bc'}`, borderRadius: 20, padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                {p.nom}
                <span style={{ marginLeft: 5, fontSize: '0.6rem', opacity: 0.7 }}>({p.count})</span>
              </button>
            );
          })}
          <button onClick={() => setProjetFilter(new Set(projetChips.map(p => p.nom)))} title="Tout sélectionner"
            style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#2e7d32', display: 'flex', alignItems: 'center' }}>
            <FaCheck size={10} />
          </button>
          <button onClick={() => setProjetFilter(new Set())} title="Tout désélectionner"
            style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#c62828', display: 'flex', alignItems: 'center' }}>
            <FaTimes size={10} />
          </button>
        </div>
      )}

      {/* ── Chips année compta ── */}
      {anneeComptaChips.length > 1 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, marginRight: 2 }}>Année Compta :</span>
          {anneeComptaChips.map(c => {
            const active = anneeComptaFilter.has(c.year);
            return (
              <button key={c.year} onClick={() => toggleAnneeCompta(c.year)}
                style={{ background: active ? '#00838f' : '#e0f7fa', color: active ? '#fff' : '#00838f', border: `1.5px solid ${active ? '#00838f' : '#80deea'}`, borderRadius: 20, padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                {c.year}
                <span style={{ marginLeft: 5, fontSize: '0.6rem', opacity: 0.7 }}>({c.count})</span>
              </button>
            );
          })}
          {anneeComptaFilter.size > 0 && (
            <button onClick={() => { setAnneeComptaFilter(new Set()); setPage(1); }}
              style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#c62828', display: 'flex', alignItems: 'center' }}>
              <FaTimes size={10} />
            </button>
          )}
        </div>
      )}

      {/* ── Chips bénéficiaires ── */}
      {beneficiaireChips.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, marginRight: 2 }}>Bénéficiaires :</span>
          {(showAllBeneficiaires ? beneficiaireChips : beneficiaireChips.slice(0, 10)).map(b => {
            const active = beneficiaireFilter.has(b.nom);
            return (
              <button key={b.nom} onClick={() => toggleBeneficiaire(b.nom)}
                style={{ background: active ? '#4a505c' : '#f5f4f2', color: active ? '#fff' : '#4a505c', border: `1.5px solid ${active ? '#4a505c' : '#d4c8bc'}`, borderRadius: 20, padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                {b.nom}
                <span style={{ marginLeft: 5, fontSize: '0.6rem', opacity: 0.7 }}>({b.count})</span>
              </button>
            );
          })}
          {beneficiaireChips.length > 10 && (
            <button onClick={() => setShowAllBeneficiaires(v => !v)}
              style={{ background: 'none', border: '1.5px solid #d4c8bc', borderRadius: 20, padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', color: '#4a505c', whiteSpace: 'nowrap' }}>
              {showAllBeneficiaires ? 'Voir moins' : `+${beneficiaireChips.length - 10}`}
            </button>
          )}
          {beneficiaireFilter.size > 0 && (
            <button onClick={() => { setBeneficiaireFilter(new Set()); setPage(1); }}
              style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#c62828', display: 'flex', alignItems: 'center' }}>
              <FaTimes size={10} />
            </button>
          )}
        </div>
      )}

      {/* ── Toggle vue + exports ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginBottom: 12 }}>
        {[
          { v: 'resume',  label: 'Résumé' },
          { v: 'encours', label: 'En cours comm.' },
          { v: 'detail',  label: 'Détail' },
        ].map(({ v, label }) => (
          <button key={v} onClick={() => { setViewMode(v); setPage(1); }}
            style={{ background: viewMode === v ? '#4a505c' : '#f5f4f2', color: viewMode === v ? '#fff' : '#4a505c', border: '1.5px solid #d4c8bc', borderRadius: 6, padding: '3px 12px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
        <div>
          <TableExportBar
            onExcel={() => exportExcel(filtered.map(r => ({
              'ID Contrat': r.contrat_id,
              'ID Bien': r.id_bien,
              'Désignation': r.designation, 'Projet': r.projet, 'Tranche': r.tranche, 'Type': r.type, 'N°': r.n,
              'Superficie (m²)': r.superficie != null ? Number(r.superficie) : '',
              'Coût/m²': r.cout_m2 > 0 ? Number(r.cout_m2) : '',
              'Coût total': r.cout_total > 0 ? Math.round(r.cout_total) : '',
              'Marge': r.cout_total > 0 ? Math.round(r.marge) : '',
              '% Marge': (r.cout_total > 0 && r.prix_net > 0) ? Number((r.marge / r.prix_net * 100).toFixed(1)) : '',
              'Statut notaire': (STATUT_CFG[getStatutKey(r)] || STATUT_CFG.NOT_YET).label,
              'Prix Net': r.prix_net, 'Avance': r.avance, 'Reliquat': r.reliquat,
              'Encaissé': r.encaisse, 'Réception': r.recept, 'PTF': r.ptf, 'Impayé': r.impaye, 'Autres': r.autres,
              'Chèque': r.mr_cheque, 'LCN': r.mr_lcn, 'Virement': r.mr_virement,
              'Compensation': r.mr_compensation, 'Crédit': r.mr_credit, 'Versement': r.mr_versement, 'Autres modes': r.mr_autres,
              'Client(s)': r.clients_noms || '', 'Tél(s)': r.clients_tels || '',
              'Commercial': r.commercial, 'Bénéficiaire': r.beneficiaire, 'TF': r.tf,
              'Année Compta': r.annee_compta || '',
              'Dernière date PTF': r.derniere_date_ptf ? fmtDate(r.derniere_date_ptf) : '',
            })), 'statut-lots')}
            onCopy={() => {
              const { html, plain } = rowsToHtml(filtered.map(r => ({
                'ID Contrat': r.contrat_id,
                'Désignation': r.designation, 'Projet': r.projet, 'Type': r.type,
                'Prix Net': fmtUnit(r.prix_net, unit), 'Avance': fmtUnit(r.avance, unit), 'Reliquat': fmtUnit(r.reliquat, unit),
                'Encaissé': fmtUnit(r.encaisse, unit), 'Réception': fmtUnit(r.recept, unit),
                'PTF': fmtUnit(r.ptf, unit), 'Impayé': fmtUnit(r.impaye, unit), 'Autres': fmtUnit(r.autres, unit),
                'Chèque': fmtUnit(r.mr_cheque, unit), 'LCN': fmtUnit(r.mr_lcn, unit), 'Virement': fmtUnit(r.mr_virement, unit),
                'Compensation': fmtUnit(r.mr_compensation, unit), 'Crédit': fmtUnit(r.mr_credit, unit),
                'Versement': fmtUnit(r.mr_versement, unit), 'Autres modes': fmtUnit(r.mr_autres, unit),
                'Client(s)': r.clients_noms || '', 'Tél(s)': r.clients_tels || '',
                'Commercial': r.commercial, 'Bénéficiaire': r.beneficiaire,
                'Année Compta': r.annee_compta || '',
              })));
              copyHtmlToClipboard(html, plain);
            }}
          />
        </div>
      </div>

      {/* ══ VUE RÉSUMÉ ══ */}
      {viewMode === 'resume' && (
        <div style={{ borderRadius: 6, border: '2px solid #d4c8bc', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...TH, textAlign: 'left', borderRight: '2px solid #d4c8bc' }}>Projet</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Nb lots</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Prix total</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Avance</th>
                  <th style={{ ...TH, textAlign: 'right', color: '#c62828' }}>Reliquat</th>
                  <th style={{ ...TH, textAlign: 'right', color: '#2e7d32' }}>Encaissé</th>
                  <th style={{ ...TH, textAlign: 'right', color: '#1565c0' }}>Réception</th>
                  <th style={{ ...TH, textAlign: 'right', color: '#6a1b9a' }}>PTF</th>
                  <th style={{ ...TH, textAlign: 'right', color: '#c62828' }}>Impayé</th>
                  <th style={{ ...TH, textAlign: 'right', color: '#546e7a', borderLeft: '2px solid #d4c8bc' }}>Autres</th>
                </tr>
              </thead>
              <tbody>
                {resumeRows.map((r, i) => (
                  <tr key={r.projet} style={{ background: i % 2 === 0 ? '#fff' : '#f9f8f7' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0ede8'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f9f8f7'}>
                    <td style={{ ...TD, fontWeight: 700, color: '#4a505c', borderRight: '2px solid #d4c8bc' }}>{r.projet}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{r.nb}</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 600 }}>{fmtUnit(r.prixNet, unit)}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#2e7d32', fontWeight: 600 }}>{fmtUnit(r.avance, unit)}</td>
                    <td style={{ ...TD, textAlign: 'right', color: r.reliquat > 0 ? '#c62828' : '#888', fontWeight: 600 }}>{fmtUnit(r.reliquat, unit)}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#2e7d32' }}>{r.encaisse > 0 ? fmtUnit(r.encaisse, unit) : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#1565c0' }}>{r.recept   > 0 ? fmtUnit(r.recept, unit)   : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#6a1b9a' }}>{r.ptf      > 0 ? fmtUnit(r.ptf, unit)      : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#c62828' }}>{r.impaye   > 0 ? fmtUnit(r.impaye, unit)   : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#546e7a', borderLeft: '2px solid #d4c8bc' }}>{r.autres > 0 ? fmtUnit(r.autres, unit) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #d4c8bc', background: '#f5f4f2' }}>
                  <td style={{ ...TD, fontWeight: 700, color: '#4a505c', borderRight: '2px solid #d4c8bc' }}>TOTAL</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{kpi.nb}</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{fmtUnit(kpi.prixTotal, unit)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#2e7d32' }}>{fmtUnit(kpi.avance, unit)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#c62828' }}>{fmtUnit(kpi.reliquat, unit)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#2e7d32' }}>{fmtUnit(resumeRows.reduce((s,r)=>s+r.encaisse,0), unit)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#1565c0' }}>{fmtUnit(resumeRows.reduce((s,r)=>s+r.recept,0), unit)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#6a1b9a' }}>{fmtUnit(resumeRows.reduce((s,r)=>s+r.ptf,0), unit)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#c62828' }}>{fmtUnit(resumeRows.reduce((s,r)=>s+r.impaye,0), unit)}</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#546e7a', borderLeft: '2px solid #d4c8bc' }}>{fmtUnit(resumeRows.reduce((s,r)=>s+r.autres,0), unit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ══ VUE EN COURS COMMERCIAL ══ */}
      {viewMode === 'encours' && (() => {
        const GRP_COLS = [
          { key: 'titre',         label: 'TF',            hdrBg: '#1b5e20', hdrColor: '#fff' },
          { key: 'nonTitre',      label: 'SANS TITRE',    hdrBg: '#b71c1c', hdrColor: '#fff' },
          { key: 'prochainement', label: 'PROCHAINEMENT', hdrBg: '#e65100', hdrColor: '#fff' },
          { key: 'total',         label: 'TOTAL',         hdrBg: '#263238', hdrColor: '#fff' },
        ];
        const SUB_COLS = ['Nb lots', 'Prix total', 'Avance', 'Reliquat'];
        const TH2 = { padding: '5px 8px', fontSize: '0.65rem', fontWeight: 700, textAlign: 'right', color: '#4a505c', background: '#f0ede8', borderBottom: '1px solid #d4c8bc', whiteSpace: 'nowrap', borderRight: '1px solid #d4c8bc' };
        const TD2 = { padding: '6px 8px', fontSize: '0.74rem', textAlign: 'right', borderBottom: '1px solid #ede9e4', borderRight: '1px solid #ede9e4', whiteSpace: 'nowrap' };
        const SEP = '2px solid #c8c0b8';

        const totals = { titre: {nb:0,prixNet:0,avance:0,reliquat:0}, nonTitre: {nb:0,prixNet:0,avance:0,reliquat:0}, prochainement: {nb:0,prixNet:0,avance:0,reliquat:0} };
        enCoursRows.forEach(r => { ['titre','nonTitre','prochainement'].forEach(g => { totals[g].nb+=r[g].nb; totals[g].prixNet+=r[g].prixNet; totals[g].avance+=r[g].avance; totals[g].reliquat+=r[g].reliquat; }); });
        totals.total = { nb: totals.titre.nb+totals.nonTitre.nb+totals.prochainement.nb, prixNet: totals.titre.prixNet+totals.nonTitre.prixNet+totals.prochainement.prixNet, avance: totals.titre.avance+totals.nonTitre.avance+totals.prochainement.avance, reliquat: totals.titre.reliquat+totals.nonTitre.reliquat+totals.prochainement.reliquat };

        const renderGroup = (grp, isTotal) => {
          const s = { fontWeight: isTotal ? 700 : 400 };
          return (<>
            <td style={{ ...TD2, ...s, borderLeft: `1px solid #c8c0b8` }}>{grp.nb || '—'}</td>
            <td style={{ ...TD2, ...s }}>{grp.prixNet > 0 ? fmtUnit(grp.prixNet, unit) : '—'}</td>
            <td style={{ ...TD2, ...s, color: grp.avance > 0 ? '#2e7d32' : '#ccc' }}>{grp.avance > 0 ? fmtUnit(grp.avance, unit) : '—'}</td>
            <td style={{ ...TD2, ...s, color: grp.reliquat > 0 ? '#c62828' : '#ccc', borderRight: SEP }}>{grp.reliquat > 0 ? fmtUnit(grp.reliquat, unit) : '—'}</td>
          </>);
        };

        const enCoursExportRows = enCoursRows.map(r => {
          const tot = { nb: r.titre.nb+r.nonTitre.nb+r.prochainement.nb, prixNet: r.titre.prixNet+r.nonTitre.prixNet+r.prochainement.prixNet, avance: r.titre.avance+r.nonTitre.avance+r.prochainement.avance, reliquat: r.titre.reliquat+r.nonTitre.reliquat+r.prochainement.reliquat };
          return {
            'Projet': r.projet,
            'TF - Nb lots': r.titre.nb, 'TF - Prix total': r.titre.prixNet, 'TF - Avance': r.titre.avance, 'TF - Reliquat': r.titre.reliquat,
            'Sans titre - Nb lots': r.nonTitre.nb, 'Sans titre - Prix total': r.nonTitre.prixNet, 'Sans titre - Avance': r.nonTitre.avance, 'Sans titre - Reliquat': r.nonTitre.reliquat,
            'Proch. - Nb lots': r.prochainement.nb, 'Proch. - Prix total': r.prochainement.prixNet, 'Proch. - Avance': r.prochainement.avance, 'Proch. - Reliquat': r.prochainement.reliquat,
            'Total - Nb lots': tot.nb, 'Total - Prix total': tot.prixNet, 'Total - Avance': tot.avance, 'Total - Reliquat': tot.reliquat,
          };
        });

        return (
          <div style={{ borderRadius: 6, border: '2px solid #d4c8bc', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 10px', background: '#f5f4f2', borderBottom: '1px solid #d4c8bc' }}>
              <TableExportBar
                onExcel={() => exportExcel(enCoursExportRows, 'en-cours-commercial')}
                onCopy={() => { const { html, plain } = rowsToHtml(enCoursExportRows.map(r => Object.fromEntries(Object.entries(r).map(([k,v]) => [k, typeof v === 'number' && k.includes('lots') ? v : typeof v === 'number' ? fmtUnit(v, unit) : v]))), 'brown'); copyHtmlToClipboard(html, plain); }}
              />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.73rem' }}>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ ...TH, textAlign: 'left', verticalAlign: 'middle', borderRight: SEP, padding: '6px 12px', background: '#363c48', color: '#fff' }}>Projet</th>
                    {GRP_COLS.map(g => (
                      <th key={g.key} colSpan={4} style={{ padding: '6px 10px', fontSize: '0.68rem', fontWeight: 800, textAlign: 'center', background: g.hdrBg, color: g.hdrColor, borderRight: SEP, letterSpacing: 0.5 }}>
                        {g.label}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {GRP_COLS.map(g => SUB_COLS.map((c, ci) => (
                      <th key={`${g.key}-${ci}`} style={{ ...TH2, borderLeft: ci === 0 ? `1px solid #c8c0b8` : undefined, borderRight: ci === 3 ? SEP : '1px solid #d4c8bc', color: c === 'Reliquat' ? '#c62828' : c === 'Avance' ? '#2e7d32' : '#4a505c' }}>
                        {c}
                      </th>
                    )))}
                  </tr>
                </thead>
                <tbody>
                  {enCoursRows.map((r, i) => {
                    const tot = { nb: r.titre.nb+r.nonTitre.nb+r.prochainement.nb, prixNet: r.titre.prixNet+r.nonTitre.prixNet+r.prochainement.prixNet, avance: r.titre.avance+r.nonTitre.avance+r.prochainement.avance, reliquat: r.titre.reliquat+r.nonTitre.reliquat+r.prochainement.reliquat };
                    const bg = i % 2 === 0 ? '#fff' : '#faf9f7';
                    return (
                      <tr key={r.projet} style={{ background: bg }}>
                        <td style={{ ...TD2, textAlign: 'left', fontWeight: 600, color: '#2a2a2a', background: '#f0ede8', borderRight: SEP, padding: '6px 12px' }}>{r.projet}</td>
                        {renderGroup(r.titre, false)}
                        {renderGroup(r.nonTitre, false)}
                        {renderGroup(r.prochainement, false)}
                        {renderGroup(tot, false)}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#363c48' }}>
                    <td style={{ ...TD2, textAlign: 'left', fontWeight: 700, color: '#fff', background: '#363c48', borderRight: SEP, padding: '6px 12px' }}>TOTAL</td>
                    {['titre','nonTitre','prochainement','total'].map(g => (<>
                      <td key={`${g}-nb`}    style={{ ...TD2, fontWeight: 700, color: '#fff', borderLeft: '1px solid rgba(255,255,255,0.15)' }}>{totals[g].nb || '—'}</td>
                      <td key={`${g}-prix`}  style={{ ...TD2, fontWeight: 700, color: '#fff' }}>{fmtUnit(totals[g].prixNet, unit)}</td>
                      <td key={`${g}-av`}    style={{ ...TD2, fontWeight: 700, color: '#81c784' }}>{fmtUnit(totals[g].avance, unit)}</td>
                      <td key={`${g}-rel`}   style={{ ...TD2, fontWeight: 700, color: '#ef9a9a', borderRight: SEP }}>{fmtUnit(totals[g].reliquat, unit)}</td>
                    </>))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ══ VUE DÉTAIL ══ */}
      {viewMode === 'detail' && (
        <div style={{ borderRadius: 6, border: '2px solid #d4c8bc', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
              <thead>
                <tr>
                  <ThS label="Contrat"       field="contrat_id" />
                  <ThS label="ID Bien"       field="id_bien" />
                  <ThS label="Désignation"   field="designation" />
                  <ThS label="Projet"        field="projet" />
                  <ThS label="Tranche"       field="tranche" />
                  <ThS label="Type"          field="type" />
                  <ThS label="N°"            field="n" />
                  <ThS label="Superficie"    field="superficie"  right />
                  <ThS label="Coût/m²"       field="cout_m2"     right />
                  <ThS label="Coût total"    field="cout_total"  right />
                  <ThS label="Marge"         field="marge"       right />
                  <ThS label="%"             field="marge_pct"   right />
                  <ThS label="Statut notaire" field="statut_notaire" />
                  <ThS label="Prix Net"      field="prix_net"      right />
                  <ThS label="Avance"        field="avance"        right />
                  <ThS label="Reliquat"      field="reliquat"      right />
                  <ThS label="Encaissé"      field="encaisse"      right />
                  <ThS label="Réception"     field="recept"        right />
                  <ThS label="PTF"           field="ptf"           right />
                  <ThS label="Impayé"        field="impaye"        right />
                  <ThS label="Autres"        field="autres"        right />
                  <ThS label="Chèque"        field="mr_cheque"     right />
                  <ThS label="LCN"           field="mr_lcn"        right />
                  <ThS label="Virement"      field="mr_virement"   right />
                  <ThS label="Compensation"  field="mr_compensation" right />
                  <ThS label="Crédit"        field="mr_credit"     right />
                  <ThS label="Versement"     field="mr_versement"  right />
                  <ThS label="Autres modes"  field="mr_autres"     right />
                  <ThS label="Dern. PTF"     field="derniere_date_ptf" />
                  <ThS label="Client(s)"     field="clients_noms" />
                  <th style={{ ...TH, textAlign: 'left' }}>Tél(s)</th>
                  <ThS label="Commercial"    field="commercial" />
                  <ThS label="Bénéficiaire"  field="beneficiaire" />
                  <ThS label="TF"            field="tf" />
                  <ThS label="An. Compta"    field="annee_compta" right />
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((r, i) => (
                  <tr key={`${r.contrat_id}-${r.id_bien}`}
                    style={{ background: i % 2 === 0 ? '#fff' : '#f9f8f7' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0ede8'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f9f8f7'}>
                    <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <a href={`https://shems.ngrok.io/consultation/${r.contrat_id}`} target="_blank" rel="noreferrer"
                        style={{ color: '#6d4e36', fontWeight: 700, textDecoration: 'none', borderBottom: '1px dashed #c8a882' }}>
                        #{r.contrat_id}
                      </a>
                    </td>
                    <td style={{ ...TD, textAlign: 'center', color: '#888', fontSize: '0.7rem' }}>{r.id_bien || '—'}</td>
                    <td style={{ ...TD, fontWeight: 600, color: '#4a505c', whiteSpace: 'nowrap' }}>{r.designation || '—'}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.projet || '—'}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap', color: '#546e7a' }}>{r.tranche || '—'}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap', color: '#546e7a' }}>{r.type || '—'}</td>
                    <td style={{ ...TD, textAlign: 'center' }}>{r.n || '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#4a505c' }}>{r.superficie != null ? Number(r.superficie).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#6d4e36' }}>{r.cout_m2 > 0 ? fmtUnit(r.cout_m2, unit) : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: r.cout_total > 0 ? '#6d4e36' : '#ccc', fontWeight: 600 }}>{r.cout_total > 0 ? fmtUnit(r.cout_total, unit) : '—'}</td>
                    {(() => { const marge = r.cout_total > 0 ? r.marge : null; const pct = (marge != null && r.prix_net > 0) ? (marge / r.prix_net * 100) : null; const col = marge == null ? '#ccc' : marge >= 0 ? '#2e7d32' : '#c62828'; return (<>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: col }}>{marge != null ? fmtUnit(marge, unit) : '—'}</td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: col }}>{pct != null ? `${pct.toFixed(1)}%` : '—'}</td>
                    </>); })()}
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                      {(() => {
                        const k = getStatutKey(r);
                        const cfg = STATUT_CFG[k];
                        return cfg
                          ? <span style={{ fontSize: '0.65rem', fontWeight: 700, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10, padding: '2px 7px', whiteSpace: 'nowrap' }}>{cfg.label}</span>
                          : '—';
                      })()}
                    </td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 600 }}>{fmtUnit(r.prix_net, unit)}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#2e7d32', fontWeight: 600 }}>{fmtUnit(r.avance, unit)}</td>
                    <td style={{ ...TD, textAlign: 'right', color: r.reliquat > 0 ? '#c62828' : '#888', fontWeight: 600 }}>{fmtUnit(r.reliquat, unit)}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#2e7d32' }}>{r.encaisse > 0 ? fmtUnit(r.encaisse, unit) : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#1565c0' }}>{r.recept   > 0 ? fmtUnit(r.recept, unit)   : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#6a1b9a' }}>{r.ptf      > 0 ? fmtUnit(r.ptf, unit)      : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#c62828' }}>{r.impaye   > 0 ? fmtUnit(r.impaye, unit)   : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#546e7a' }}>{r.autres   > 0 ? fmtUnit(r.autres, unit)   : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#1565c0' }}>{r.mr_cheque       > 0 ? fmtUnit(r.mr_cheque, unit)       : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#6a1b9a' }}>{r.mr_lcn          > 0 ? fmtUnit(r.mr_lcn, unit)          : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#2e7d32' }}>{r.mr_virement     > 0 ? fmtUnit(r.mr_virement, unit)     : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#e65100' }}>{r.mr_compensation > 0 ? fmtUnit(r.mr_compensation, unit) : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#00838f' }}>{r.mr_credit       > 0 ? fmtUnit(r.mr_credit, unit)       : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#827717' }}>{r.mr_versement    > 0 ? fmtUnit(r.mr_versement, unit)    : '—'}</td>
                    <td style={{ ...TD, textAlign: 'right', color: '#546e7a' }}>{r.mr_autres       > 0 ? fmtUnit(r.mr_autres, unit)       : '—'}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap', color: '#6a1b9a', fontSize: '0.68rem' }}>{r.derniere_date_ptf ? fmtDate(r.derniere_date_ptf) : '—'}</td>
                    <td style={{ ...TD, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.clients_noms || '—'}</td>
                    <td style={TD}><TelCell tels={r.clients_tels} /></td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.commercial || '—'}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.beneficiaire || '—'}</td>
                    <td style={{ ...TD, textAlign: 'center' }}>{r.tf || '—'}</td>
                    <td style={{ ...TD, textAlign: 'center', fontWeight: 600, color: '#00838f' }}>{r.annee_compta || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sorted.length > PAGE_SIZE && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderTop: '1px solid #ede9e4', background: '#faf9f7' }}>
              <span style={{ fontSize: '0.72rem', color: '#aaa' }}>
                <span style={{ color: '#4a505c', fontWeight: 600 }}>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)}</span> sur {sorted.length}
              </span>
              <div style={{ display: 'flex', gap: 3 }}>
                {[{ l: '«', t: 1 }, { l: '‹', t: page - 1 }, { l: '›', t: page + 1 }, { l: '»', t: totalPages }].map(({ l, t }) => (
                  <button key={l} onClick={() => setPage(Math.max(1, Math.min(totalPages, t)))}
                    disabled={t < 1 || t > totalPages || t === page}
                    style={{ minWidth: 26, height: 24, border: '1px solid #d4c8bc', borderRadius: 5, background: t === page ? '#4a505c' : '#fff', color: t === page ? '#fff' : '#4a505c', cursor: t < 1 || t > totalPages ? 'default' : 'pointer', fontSize: '0.72rem' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatutLotsSection;
